// src/components/Footer.js
import React from 'react';
import styled, { keyframes } from 'styled-components';

/* ---------- styles ---------- */
const FooterSection = styled.footer`
  background: #ffffff;
  border-top: 1px solid #e5e7eb;
  padding: 2.5rem 0 2rem;

  /* 모바일에서 하단 안전영역(노치) 고려 */
  padding-bottom: max(2rem, env(safe-area-inset-bottom));
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem; /* 기존 값 유지 */

  @media (max-width: 768px) {
    padding: 0 1.25rem; /* 살짝만 축소 */
  }
`;

/* 무한 스크롤 뷰포트 */
const Viewport = styled.div`
  position: relative;
  overflow: hidden;
  margin-bottom: 1.25rem;

  /* 가장자리 자연스러운 페이드 (지원 브라우저에서만) */
  mask-image: linear-gradient(
    to right,
    rgba(0,0,0,0) 0%,
    rgba(0,0,0,1) 6%,
    rgba(0,0,0,1) 94%,
    rgba(0,0,0,0) 100%
  );

  @media (max-width: 768px) {
    margin-bottom: 1rem; /* 모바일에서 약간 축소 */
  }
`;

/* 트랙 애니메이션 */
const marquee = keyframes`
  0%   { transform: translateX(0); }
  100% { transform: translateX(-50%); } /* 2배로 중복시 절반만 이동하면 무한루프 */
`;

const Track = styled.div`
  display: flex;
  gap: 24px;
  width: max-content;
  will-change: transform;
  animation: ${marquee} var(--duration, 36s) linear infinite;

  /* 호버시 일시정지 */
  &:hover { animation-play-state: paused; }

  /* 접근성: 모션 줄이기 */
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

  @media (max-width: 1024px) {
    gap: 20px;
  }
  @media (max-width: 768px) {
    gap: 16px; /* 모바일에서 로고 간격 조금 압축 */
  }
`;

/* 각 로고 셀 */
const LogoCell = styled.a`
  flex: 0 0 auto;
  height: 64px;
  min-width: 150px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 10px;
  border-radius: 10px;
  background: #fff;
  text-decoration: none;

  /* 로고 보정용 높이 프리셋 */
  --h: 34px;
  &[data-size='sm'] { --h: 26px; }
  &[data-size='md'] { --h: 34px; }
  &[data-size='lg'] { --h: 42px; }
  &[data-size='xl'] { --h: 52px; }
  &[data-size='2xl'] { --h: 96px; }

  img {
    max-height: var(--h);
    max-width: 100%;
    width: auto;
    height: auto;
    object-fit: contain;
    filter: none; /* 컬러 유지 */
    transition: transform .2s ease, opacity .2s ease;
    opacity: .95;
    display: block;
  }

  &:hover img {
    transform: translateY(-1px);
    opacity: 1;
  }

  /* ====== 반응형 미세 조정 (원본 느낌 유지) ====== */
  @media (max-width: 1024px) {
    height: 58px;
    min-width: 132px;
    padding: 6px 8px;
  }
  @media (max-width: 768px) {
    height: 52px;
    min-width: 120px;
    padding: 6px 8px;

    /* 작은 화면에서는 프리셋을 살짝 축소 적용 */
    &[data-size='sm'] { --h: 22px; }
    &[data-size='md'] { --h: 28px; }
    &[data-size='lg'] { --h: 34px; }
    &[data-size='xl'] { --h: 42px; }
    &[data-size='2xl'] { --h: 64px; }
  }
`;

const Copy = styled.div`
  text-align: center;
  font-size: 0.95rem;
  color: #94a3b8;

  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

/* ---------- component ---------- */
function Footer() {
  const logos = [
    { src: '/images/logo/venture.webp', alt: '벤처나라', href: '#', size: 'md' },
    { src: '/images/logo/disaster.webp', alt: '재난안전인증', href: '#', size: 'xl' },
    { src: '/images/logo/ventureGoods.webp', alt: '벤처창업혁신상품', href: '#', size: 'xl' },
    { src: '/images/logo/social.webp', alt: '사회적기업', href: '#', size: '2xl' },
    { src: '/images/logo/Nara.webp', alt: '나라장터', href: '#', size: 'lg' },
    { src: '/images/logo/KSMark.webp', alt: 'KS마크', href: '#', size: 'xl' },
    { src: '/images/logo/koreaRoad.webp', alt: '한국도로공사', href: '#', size: 'md' },
    { src: '/images/logo/Police.webp', alt: '경찰청', href: '#', size: 'md' },
  ];

  // 마키는 동일 콘텐츠를 2번 이어붙여야 매끄럽게 반복됨
  const doubled = [...logos, ...logos];

  return (
    <FooterSection>
      <Container>
        <Viewport>
          {/* 속도 조절: --duration 값을 늘리면 느리게(예: 42s), 줄이면 빠르게(예: 28s) */}
          <Track style={{ '--duration': '36s' }}>
            {doubled.map((logo, i) => {
              const isLink = Boolean(logo.href);
              return (
                <LogoCell
                  key={i}
                  as={isLink ? 'a' : 'span'}
                  href={isLink ? logo.href : undefined}
                  target={isLink ? '_blank' : undefined}
                  rel={isLink ? 'noopener noreferrer' : undefined}
                  data-size={logo.size || 'md'}
                  aria-label={logo.alt}
                >
                  <img
                    src={logo.src}
                    alt={logo.alt}
                    loading="lazy"
                    onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
                  />
                </LogoCell>
              );
            })}
          </Track>
        </Viewport>

        <Copy>© 2025 SEYEON ENS. All rights reserved.</Copy>
      </Container>
    </FooterSection>
  );
}

export default Footer;
